import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Radar, RefreshCw, Trash2, CheckCircle, Eye, Linkedin, MessageCircle, Link2, Image as ImageIcon } from 'lucide-react';
import axios from 'axios';
import '@/Radar.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('de-AT', { day: '2-digit', month: 'long', year: 'numeric' }) : '');

const EditionBadge = ({ edition }) => (
  edition === 'austria'
    ? <span className="radar-edition radar-edition-austria" data-testid="edition-badge-austria">🇦🇹 Österreich Spezial</span>
    : <span className="radar-edition radar-edition-world" data-testid="edition-badge-world">🌍 Weltweit</span>
);

function ShareBar({ post }) {
  const [copied, setCopied] = useState(false);
  const url = `${API}/radar/share/${post.slug}`;
  const text = `KI-Video Radar: ${post.title}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard blocked */ }
  };

  return (
    <div className="radar-share" data-testid="radar-share-bar">
      <span className="radar-share-label">Ausgabe teilen:</span>
      <a
        href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`}
        target="_blank" rel="noopener noreferrer"
        className="radar-share-btn radar-share-linkedin"
        data-testid="radar-share-linkedin"
      >
        <Linkedin size={16} /> LinkedIn
      </a>
      <a
        href={`https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`}
        target="_blank" rel="noopener noreferrer"
        className="radar-share-btn radar-share-whatsapp"
        data-testid="radar-share-whatsapp"
      >
        <MessageCircle size={16} /> WhatsApp
      </a>
      <button onClick={copy} className="radar-share-btn radar-share-copy" data-testid="radar-share-copy">
        <Link2 size={16} /> {copied ? 'Kopiert!' : 'Link kopieren'}
      </button>
    </div>
  );
}


export function RadarList() {
  const [posts, setPosts] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    document.title = 'KI-News & AI-Video Radar Österreich | mAIsblau Wien';
    document.querySelector('meta[name="description"]')?.setAttribute('content', 'KI-Video Radar Österreich: Wöchentlich kuratierte KI-News mit Einordnung fürs Video-Marketing – von mAIsblau, AI Videoproduktion Wien.');
    window.scrollTo(0, 0);
    axios.get(`${API}/radar/posts`).then((r) => setPosts(r.data)).catch(() => setPosts([]));
  }, []);

  return (
    <div className="radar-page" data-testid="radar-list-page">
      <div className="container">
        <Link to="/" className="sp-back" data-testid="radar-back-home"><ArrowLeft size={16} /> Zur Startseite</Link>
        <h1 className="sp-title"><Radar size={34} className="radar-icon" /> KI-Video Radar</h1>
        <p className="sp-intro">
          Wöchentlich kuratiert: die wichtigsten KI-News für Unternehmen in Wien & Österreich – 
          mit unserer Einordnung, was sie für dein Video-Marketing bedeuten.
        </p>
        {posts === null && <p className="radar-empty">Lade…</p>}
        {posts !== null && posts.length === 0 && (
          <p className="radar-empty" data-testid="radar-empty">Die erste Ausgabe erscheint in Kürze – schau bald wieder vorbei!</p>
        )}
        {posts !== null && posts.length > 0 && (
          <div className="radar-filter" data-testid="radar-filter-tabs">
            {[['all', 'Alle'], ['world', '🌍 Weltweit'], ['austria', '🇦🇹 Österreich']].map(([val, label]) => (
              <button
                key={val}
                className={`radar-filter-btn ${filter === val ? 'active' : ''}`}
                onClick={() => setFilter(val)}
                data-testid={`radar-filter-${val}`}
              >{label}</button>
            ))}
          </div>
        )}
        <div className="radar-list">
          {(posts || []).filter((p) => filter === 'all' || (p.edition || 'world') === filter).map((p) => (
            <Link to={`/ki-news/${p.slug}`} className={`radar-card ${p.has_image ? 'radar-card-with-thumb' : ''}`} key={p.id} data-testid={`radar-card-${p.slug}`}>
              {p.has_image && <img src={`${API}/radar/image/${p.id}`} alt="" className="radar-thumb" loading="lazy" />}
              <div className="radar-card-body">
                <div className="radar-card-meta">
                  <EditionBadge edition={p.edition || 'world'} />
                  <span className="radar-week">{p.week_label} · {fmtDate(p.published_at)}</span>
                </div>
                <h2>{p.title}</h2>
                <p>{p.intro}</p>
                <span className="radar-readmore">Weiterlesen →</span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

export function RadarPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const key = sessionStorage.getItem('radar_admin_key') || '';
    axios.get(`${API}/radar/posts/${slug}`, { headers: { 'X-Admin-Key': key } })
      .then((r) => {
        setPost(r.data);
        document.title = `${r.data.title} | KI-Video Radar | mAIsblau`;
      })
      .catch(() => setError(true));
  }, [slug]);

  if (error) return (
    <div className="radar-page"><div className="container">
      <Link to="/ki-news" className="sp-back"><ArrowLeft size={16} /> Alle Ausgaben</Link>
      <p className="radar-empty">Diese Ausgabe wurde nicht gefunden.</p>
    </div></div>
  );
  if (!post) return <div className="radar-page"><div className="container"><p className="radar-empty">Lade…</p></div></div>;

  return (
    <div className="radar-page" data-testid="radar-post-page">
      <div className="container">
        <Link to="/ki-news" className="sp-back" data-testid="radar-back-list"><ArrowLeft size={16} /> Alle Ausgaben</Link>
        <div className="radar-card-meta">
          <EditionBadge edition={post.edition || 'world'} />
          <span className="radar-week">{post.week_label}{post.published_at ? ` · ${fmtDate(post.published_at)}` : ' · ENTWURF'}</span>
        </div>
        {post.has_image && (
          <img src={`${API}/radar/image/${post.id}`} alt={post.title} className="radar-cover" data-testid="radar-cover-image" />
        )}
        <h1 className="sp-title radar-post-title">{post.title}</h1>
        <p className="sp-intro">{post.intro}</p>

        {post.items.map((item, i) => (
          <article className="radar-item" key={i} data-testid={`radar-item-${i}`}>
            <h2>{item.headline}</h2>
            <p className="radar-summary">{item.summary}</p>
            <div className="radar-take">
              <span className="radar-take-label">Maisblau-Einordnung</span>
              <p>{item.take}</p>
            </div>
            <a href={item.source_url} target="_blank" rel="noopener noreferrer" className="radar-source">
              <ExternalLink size={14} /> Quelle: {item.source_name}
            </a>
          </article>
        ))}

        <ShareBar post={post} />

        <div className="sp-price-box radar-cta-box">
          <p className="sp-price">Du willst KI-Video für dein Unternehmen nutzen, statt nur darüber zu lesen?</p>
          <Link to="/#contact" className="sp-cta" data-testid="radar-cta">Jetzt Projekt anfragen</Link>
        </div>

        <div className="sp-footer">
          <span>© 2026 mAIsblau Videoproduktion Wien</span>
          <Link to="/impressum">Impressum</Link>
          <Link to="/datenschutz">Datenschutz</Link>
        </div>
      </div>
    </div>
  );
}

export function RadarAdmin() {
  const [key, setKey] = useState(sessionStorage.getItem('radar_admin_key') || '');
  const [posts, setPosts] = useState(null);
  const [msg, setMsg] = useState('');
  const [generating, setGenerating] = useState(false);
  const [usage, setUsage] = useState(null);

  const headers = { 'X-Admin-Key': key };

  const loadUsage = (h) => {
    axios.get(`${API}/radar/admin/usage`, { headers: h }).then((r) => setUsage(r.data)).catch(() => {});
  };

  const load = () => {
    axios.get(`${API}/radar/admin/posts`, { headers })
      .then((r) => { setPosts(r.data); setMsg(''); sessionStorage.setItem('radar_admin_key', key); loadUsage(headers); })
      .catch(() => { setPosts(null); setMsg('Ungültiger Admin-Schlüssel'); });
  };

  useEffect(() => {
    document.title = 'Radar Admin | mAIsblau';
    if (key) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generate = async (edition) => {
    setGenerating(true);
    setMsg(`${edition === 'austria' ? 'Österreich-Spezial' : 'Weltweit'}-Digest wird erstellt – das dauert ca. 1 Minute…`);
    try {
      await axios.post(`${API}/radar/admin/generate?edition=${edition}`, {}, { headers });
      let tries = 0;
      const before = posts ? posts.length : 0;
      const poll = setInterval(async () => {
        tries += 1;
        const r = await axios.get(`${API}/radar/admin/posts`, { headers });
        if (r.data.length > before || tries > 24) {
          clearInterval(poll);
          setPosts(r.data);
          setGenerating(false);
          loadUsage(headers);
          setMsg(r.data.length > before ? 'Neuer Entwurf ist da!' : 'Kein neuer Entwurf – bitte Backend-Logs prüfen.');
        }
      }, 5000);
    } catch (e) {
      setGenerating(false);
      setMsg(e.response?.data?.detail || 'Fehler beim Start der Generierung');
    }
  };

  const publish = async (id) => {
    await axios.post(`${API}/radar/admin/publish/${id}`, {}, { headers });
    load();
  };

  const remove = async (id) => {
    if (!window.confirm('Diesen Digest wirklich löschen?')) return;
    await axios.delete(`${API}/radar/admin/posts/${id}`, { headers });
    load();
  };

  const makeImage = async (id) => {
    setMsg('Vorschaubild wird erstellt – ca. 20 Sekunden…');
    try {
      await axios.post(`${API}/radar/admin/image/${id}`, {}, { headers });
      setMsg('Vorschaubild erstellt!');
      load();
    } catch {
      setMsg('Bildgenerierung fehlgeschlagen – bitte nochmal versuchen.');
    }
  };

  return (
    <div className="radar-page" data-testid="radar-admin-page">
      <div className="container">
        <Link to="/ki-news" className="sp-back"><ArrowLeft size={16} /> Zum öffentlichen Radar</Link>
        <h1 className="sp-title">Radar Admin</h1>

        <div className="radar-admin-bar">
          <input
            type="password"
            placeholder="Admin-Schlüssel"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
            data-testid="radar-admin-key-input"
          />
          <button onClick={load} data-testid="radar-admin-login">Entsperren</button>
          <button onClick={() => generate('world')} disabled={generating || posts === null || (usage && usage.used >= usage.limit)} className="radar-generate" data-testid="radar-admin-generate">
            <RefreshCw size={16} className={generating ? 'radar-spin' : ''} /> 🌍 Weltweit-Digest
          </button>
          <button onClick={() => generate('austria')} disabled={generating || posts === null || (usage && usage.used >= usage.limit)} className="radar-generate radar-generate-at" data-testid="radar-admin-generate-austria">
            <RefreshCw size={16} className={generating ? 'radar-spin' : ''} /> 🇦🇹 Österreich-Digest
          </button>
          {usage && (
            <span className="radar-usage" data-testid="radar-usage-counter">
              Diese Woche: {usage.used}/{usage.limit} Generierungen
            </span>
          )}
        </div>
        {msg && <p className="radar-msg" data-testid="radar-admin-msg">{msg}</p>}

        {posts && posts.map((p) => (
          <div className="radar-admin-card" key={p.id} data-testid={`radar-admin-card-${p.id}`}>
            <div className="radar-admin-info">
              <span className={`radar-status radar-status-${p.status}`}>{p.status === 'draft' ? 'ENTWURF' : 'VERÖFFENTLICHT'}</span>
              <EditionBadge edition={p.edition || 'world'} />
              <h3>{p.title}</h3>
              <span className="radar-week">{p.week_label} · erstellt {fmtDate(p.created_at)}</span>
            </div>
            <div className="radar-admin-actions">
              <Link to={`/ki-news/${p.slug}`} className="radar-btn" data-testid={`radar-preview-${p.id}`}><Eye size={15} /> Vorschau</Link>
              <button className="radar-btn" onClick={() => makeImage(p.id)} data-testid={`radar-image-${p.id}`}>
                <ImageIcon size={15} /> {p.has_image ? 'Bild neu' : 'Bild erstellen'}
              </button>
              {p.status === 'draft' && (
                <button className="radar-btn radar-btn-publish" onClick={() => publish(p.id)} data-testid={`radar-publish-${p.id}`}>
                  <CheckCircle size={15} /> Veröffentlichen
                </button>
              )}
              <button className="radar-btn radar-btn-delete" onClick={() => remove(p.id)} data-testid={`radar-delete-${p.id}`}>
                <Trash2 size={15} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
